import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import PreviewCanvas from './PreviewCanvas';
import AiBanner from './AiBanner';
import { useBlockApply } from './useBlockApply';
import { GetVideoWorkspaceUploadUrl } from '../../../Client/ManagementServiceClient';
import { uploadToSession } from '../../../Client/resumableUpload';
import {
    OVERLAY_TEMPLATES,
    canvasToPngBlob,
    getOverlayTemplate,
    renderTemplateCanvas,
    templateDefaults,
} from './overlayTemplates';
import './effectBlocks.css';

const ANIMATIONS = [
    { value: 'none', label: 'None (just appears)' },
    { value: 'top', label: 'Slide in from top' },
    { value: 'bottom', label: 'Slide in from bottom' },
    { value: 'left', label: 'Slide in from left' },
    { value: 'right', label: 'Slide in from right' },
];

/**
 * OverlayBlockEditor — superimpose a generated graphic (phone notification,
 * call screen, location card) on the clip.
 *
 * Unlike the sticker block, the image isn't an upload the user has to make
 * elsewhere: the picked template is drawn to a canvas here, so the preview and
 * the uploaded PNG come from the same render. On apply the PNG is uploaded as a
 * workspace asset and compiled through the `overlay` block (scale + overlay),
 * so approval, execution and billing are the unchanged path.
 *
 * Props:
 *   videoWorkspaceId, frameUrl, sourceFileName, mediaInfo - the target video
 *   templateId  - which template to open on ('notification' | 'call' | 'location')
 *   aiSuggestion - optional { params, rationale } to seed from a suggestion
 *   onApplied, onClose
 */
function OverlayBlockEditor({
    videoWorkspaceId, frameUrl, sourceFileName, mediaInfo, templateId = 'notification',
    aiSuggestion, onApplied, onClose,
}) {
    const [template, setTemplate] = useState(() => getOverlayTemplate(templateId));
    const [values, setValues] = useState(() => templateDefaults(getOverlayTemplate(templateId)));
    const [placement, setPlacement] = useState(() => {
        const t = getOverlayTemplate(templateId);
        return { ...t.defaults, ...(aiSuggestion?.params || {}) };
    });
    const [imageUrl, setImageUrl] = useState('');
    const [uploading, setUploading] = useState(false);

    const { measured, setMeasured, aspectRatio, ready, applying, error, setError, apply } =
        useBlockApply({ videoWorkspaceId, sourceFileName, mediaInfo });

    const clipDuration = Number(measured?.duration) || 0;

    // Re-render the graphic whenever the template or its fields change. A fixed
    // preview width is plenty on screen; apply re-renders at true output size.
    useEffect(() => {
        setImageUrl(renderTemplateCanvas(template, values, 1000).toDataURL('image/png'));
    }, [template, values]);

    const pickTemplate = useCallback((id) => {
        const next = getOverlayTemplate(id);
        setTemplate(next);
        setValues(templateDefaults(next));
        setPlacement({ ...next.defaults });
        setError('');
    }, [setError]);

    const setField = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));
    const setPlace = (patch) => setPlacement((prev) => ({ ...prev, ...patch }));

    // The block drives the draggable preview. `scale` is a fraction of the video
    // width, matching how the compiler sizes the overlay.
    const block = useMemo(() => ({
        id: 'overlay-1',
        type: 'overlay',
        source: sourceFileName,
        params: { x: placement.x, y: placement.y, scale: placement.scale },
    }), [placement.x, placement.y, placement.scale, sourceFileName]);

    const handleApply = async () => {
        if (!ready) {
            setError('Make sure the clip has loaded.');
            return;
        }
        setUploading(true);
        setError('');
        try {
            // Render at the exact pixel width the overlay will occupy, so text
            // stays crisp instead of being upscaled from the preview.
            let targetWidth = Math.round(placement.scale * Number(measured.width));
            targetWidth = Math.max(2, targetWidth - (targetWidth % 2));
            const canvas = renderTemplateCanvas(template, values, targetWidth);
            const blob = await canvasToPngBlob(canvas);

            const idToken = await getAuth().currentUser.getIdToken();
            const alias = `overlay-${template.id}-${Date.now().toString(36)}`;
            const urlRes = await GetVideoWorkspaceUploadUrl(idToken, {
                VideoWorkspaceId: videoWorkspaceId,
                FileName: `${alias}.png`,
                FileAlias: alias,
                SizeBytes: blob.size,
            });
            if (urlRes.status !== 200) {
                setError(urlRes.myJson?.detail || 'Could not prepare the overlay upload.');
                return;
            }

            // The URL is a resumable session; a PNG this size goes in one slice.
            try {
                await uploadToSession({ sessionUri: urlRes.myJson.UploadUrl, file: blob });
            } catch (err) {
                setError(`Could not upload the overlay image (${err.message}).`);
                return;
            }

            const fileName = urlRes.myJson.FileName;
            const endTime = placement.duration > 0 ? Number(placement.start || 0) + Number(placement.duration) : null;
            await apply({
                type: 'overlay',
                startTime: Number(placement.start || 0),
                endTime,
                params: {
                    overlayAsset: fileName,
                    x: placement.x,
                    y: placement.y,
                    scale: placement.scale,
                    animate: placement.animate || 'none',
                    animDuration: 0.4,
                    label: template.summary(values),
                },
                extraMedia: {
                    [fileName]: { width: canvas.width, height: canvas.height, duration: 0 },
                },
            }, onApplied);
        } catch (err) {
            setError('Error adding the overlay: ' + err.message);
        } finally {
            setUploading(false);
        }
    };

    const busy = applying || uploading;

    return (
        <div className="eb-editor">
            {aiSuggestion?.rationale && <AiBanner rationale={aiSuggestion.rationale} />}
            <div className="eb-editor-grid">
                <PreviewCanvas
                    frameUrl={frameUrl}
                    isVideo
                    aspectRatio={aspectRatio}
                    blocks={[block]}
                    selectedId="overlay-1"
                    stickerUrl={imageUrl}
                    onChange={(_id, next) => setPlace({ x: next.x, y: next.y })}
                    onSelect={() => {}}
                    onMeta={setMeasured}
                />
                <div className="eb-controls">
                    <h5>Superimpose</h5>

                    <div className="eb-template-picker" role="group" aria-label="Overlay template">
                        {OVERLAY_TEMPLATES.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                className={`eb-template${t.id === template.id ? ' selected' : ''}`}
                                onClick={() => pickTemplate(t.id)}
                            >
                                <span aria-hidden="true">{t.icon}</span>
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {template.fields.map((field) => (
                        <label className="eb-ctl" key={field.key}>
                            <span>{field.label}</span>
                            {field.type === 'textarea' ? (
                                <textarea
                                    className="eb-input"
                                    rows={2}
                                    maxLength={field.max}
                                    placeholder={field.placeholder}
                                    value={values[field.key] ?? ''}
                                    onChange={(e) => setField(field.key, e.target.value)}
                                />
                            ) : field.type === 'select' ? (
                                <select
                                    className="eb-input"
                                    value={values[field.key] ?? field.options[0].value}
                                    onChange={(e) => setField(field.key, e.target.value)}
                                >
                                    {field.options.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            ) : field.type === 'checkbox' ? (
                                <input
                                    type="checkbox"
                                    checked={Boolean(values[field.key])}
                                    onChange={(e) => setField(field.key, e.target.checked)}
                                />
                            ) : (
                                <input
                                    className="eb-input"
                                    type="text"
                                    maxLength={field.max}
                                    placeholder={field.placeholder}
                                    value={values[field.key] ?? ''}
                                    onChange={(e) => setField(field.key, e.target.value)}
                                />
                            )}
                        </label>
                    ))}

                    <label className="eb-ctl">
                        <span>Size <em>{Math.round(placement.scale * 100)}%</em></span>
                        <input
                            type="range" min="15" max="100" step="1"
                            value={placement.scale * 100}
                            onChange={(e) => setPlace({ scale: Number(e.target.value) / 100 })}
                        />
                    </label>

                    <label className="eb-ctl">
                        <span>Appears at <em>{(placement.start || 0).toFixed(1)}s</em></span>
                        <input
                            type="range" min="0" max={Math.max(1, Math.floor(clipDuration))} step="0.1"
                            value={placement.start || 0}
                            onChange={(e) => setPlace({ start: Number(e.target.value) })}
                        />
                    </label>

                    <label className="eb-ctl">
                        <span>On screen for <em>{placement.duration > 0 ? `${Number(placement.duration).toFixed(1)}s` : 'rest of clip'}</em></span>
                        <input
                            type="range" min="0" max="15" step="0.5"
                            value={placement.duration || 0}
                            onChange={(e) => setPlace({ duration: Number(e.target.value) })}
                        />
                    </label>

                    <label className="eb-ctl">
                        <span>Entrance</span>
                        <select
                            className="eb-input"
                            value={placement.animate || 'none'}
                            onChange={(e) => setPlace({ animate: e.target.value })}
                        >
                            {ANIMATIONS.map((a) => (
                                <option key={a.value} value={a.value}>{a.label}</option>
                            ))}
                        </select>
                    </label>

                    <p className="eb-hint">Drag the graphic on the canvas to position it.</p>

                    {error && <p className="eb-error">{error}</p>}

                    <div className="eb-actions">
                        {onClose && <button type="button" className="eb-btn" onClick={onClose}>Cancel</button>}
                        <button type="button" className="eb-btn primary" disabled={!ready || busy} onClick={handleApply}>
                            {uploading ? 'Uploading…' : applying ? 'Applying…' : 'Add to timeline'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OverlayBlockEditor;
